import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Chip, IconButton, Skeleton, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import { colors } from "brand";
import { useTranslation } from "i18n-fe";
import { useAnnouncerContext } from "../context/AnnouncerContext";
import { useRingData } from "../context/RingDataContext";
import { buildDaySpine, shiftDayKey, formatDaySwitcherLabel } from "../utils/daySpine";
import { formatClassNumber } from "../utils/formatClassNumber";
import { formatTime } from "../utils/formatTime";

// #2140 (docs/score/announcer-ui-redesign.md §4.2) — band 2, "the day": a
// non-scrolling vertical list of the selected ring's CLASSES, one venue-local
// day at a time. Consumes classes/rides/breaks/liveClassId/summaryByClassId/
// isClassPlacedFromSummary straight off the EXISTING RingDataProvider poll set
// (useRingData()) — this component adds NO new hook and opens NO new poll
// (docs/score/announcer-ui-redesign.md §11, #2137's whole point).
//
// #3392 (Announcer Live Desk redesign, part 1): class rows are "icon, CL number,
// name" and nothing else. No "x min" countdowns, no classes-done footer, no
// per-row status line. #3449 brings break rows back between the class rows
// (dashed orange, fork-and-knife, like the Stream's BreakRow). Placed class = trophy, live
// class = filled GREEN dot (never red: red reads as an error), others = empty
// dot. Nothing blinks.

// Fixed pixel heights for the header and the two row densities the
// compress ladder switches between. AnnouncerFrame's own `frame-band-day`
// wrapper is `overflow:hidden` by contract (#2138) — this component nests its
// OWN scroller (the rows container below) rather than touching that.
const HEADER_HEIGHT = 44;
const ROW_HEIGHT_NORMAL = 36;
const ROW_HEIGHT_COMPRESSED = 24;
const ICON_SLOT = 18;

// The 18px icon slot at the front of each class row. Placed wins over live:
// a placed class is finished, so the trophy is the truer cue.
function ClassIcon({ classId, placed, isRunning, t }) {
  if (placed) {
    return (
      <EmojiEventsIcon
        data-testid={`day-spine-placed-${classId}`}
        data-icon="trophy"
        role="img"
        aria-label={t("daySpine.placed")}
        titleAccess={t("daySpine.placed")}
        sx={{ fontSize: ICON_SLOT, flexShrink: 0, color: colors.foxOrange }}
      />
    );
  }
  const base = { width: 12, height: 12, borderRadius: "50%", boxSizing: "border-box" };
  return (
    <Box sx={{ width: ICON_SLOT, flexShrink: 0, display: "grid", placeItems: "center" }}>
      {isRunning ? (
        <Box
          data-testid="day-spine-marker-dot"
          data-dot="live"
          data-color="success"
          role="img"
          aria-label={t("daySpine.live")}
          sx={{ ...base, bgcolor: "success.main", border: "2px solid", borderColor: "success.main" }}
        />
      ) : (
        <Box
          data-testid="day-spine-marker-dot"
          data-dot="empty"
          sx={{ ...base, border: "2px solid", borderColor: "text.secondary" }}
        />
      )}
    </Box>
  );
}

// #3449 -- a break between classes: the Stream BreakRow's dashed orange border
// and fork-and-knife icon at the day list's own row height. Not a button, no
// click handler, no countdown.
function BreakEntryRow({ entry, density, t }) {
  const rowHeight = density === "compressed" ? ROW_HEIGHT_COMPRESSED : ROW_HEIGHT_NORMAL;
  const label = entry.label || t("oog.breakLabel");
  const time = formatTime(entry.scheduledTime, entry.scheduledTimeZone);
  return (
    <Box
      data-testid={`day-spine-break-${entry.id}`}
      data-kind="break"
      title={`${time} ${label}`.trim()}
      sx={{
        height: rowHeight,
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        gap: 1,
        mx: 0.75,
        px: 0.75,
        border: "1px dashed",
        borderColor: colors.foxOrange,
        borderRadius: 1,
      }}
    >
      <RestaurantIcon sx={{ color: colors.foxOrange, fontSize: ICON_SLOT, flexShrink: 0 }} />
      <Typography
        data-testid="day-spine-break-time"
        variant="caption"
        noWrap
        sx={{ fontWeight: 700, flexShrink: 0, lineHeight: 1.2 }}
      >
        {time}
      </Typography>
      <Typography
        data-testid="day-spine-break-label"
        variant="caption"
        noWrap
        sx={{ fontWeight: 800, flex: 1, minWidth: 0, lineHeight: 1.2 }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function ClassRow({ entry, isViewing, density, onSelect, rowRef, t }) {
  const rowHeight = density === "compressed" ? ROW_HEIGHT_COMPRESSED : ROW_HEIGHT_NORMAL;
  const label = `${formatClassNumber(entry.classNumber)} ${entry.className}`.trim();
  return (
    <Box
      ref={rowRef}
      data-testid={`day-spine-row-${entry.classId}`}
      data-marker={entry.marker}
      data-running={entry.isRunning ? "true" : "false"}
      data-viewing={isViewing ? "true" : "false"}
      data-placed={entry.placed ? "true" : "false"}
      aria-current={isViewing ? "true" : undefined}
      onClick={onSelect}
      title={label}
      sx={{
        height: rowHeight,
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        pl: 1.25,
        pr: 1,
        cursor: "pointer",
        borderLeft: "3px solid",
        borderLeftColor: isViewing ? colors.foxOrange : "transparent",
        bgcolor: isViewing ? "rgba(248, 153, 29, 0.12)" : "transparent",
        "&:hover": { bgcolor: isViewing ? "rgba(248, 153, 29, 0.12)" : "action.hover" },
      }}
    >
      <ClassIcon classId={entry.classId} placed={entry.placed} isRunning={entry.isRunning} t={t} />
      <Typography
        data-testid="day-spine-row-label"
        variant={density === "compressed" ? "caption" : "body2"}
        noWrap
        sx={{
          flex: 1,
          minWidth: 0,
          fontWeight: 500,
          lineHeight: 1.2,
          color: entry.isRunning ? "success.main" : "text.primary",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

export default function DaySpine() {
  const { t } = useTranslation("announcer");
  const { selectedRing, viewingClassId, setViewingClassId, openLookup } = useAnnouncerContext();
  const {
    classes,
    rides,
    breaks,
    statuses,
    liveClassId,
    isClassPlacedFromSummary,
    todayKey,
    regionLoading,
    hasFetchError,
  } = useRingData();
  // #2703 -- the list is built from classes + rides + breaks: until all
  // three have answered, a blank list is indistinguishable from "no classes
  // today", so the rows area says it is loading instead.
  const rowsLoading = regionLoading.classes || regionLoading.rides || regionLoading.breaks;

  // Band-2-owned (doc §4.2) — a LAZY initializer capturing todayKey exactly
  // ONCE at mount, never recomputed from a ticking clock (AC10: the shown
  // day must never auto-advance on its own, including at a real midnight
  // rollover mid-session). Reset only on an explicit ring switch below.
  const [shownDayKey, setShownDayKey] = useState(() => todayKey);
  const previousRingRef = useRef(selectedRing);
  useEffect(() => {
    if (previousRingRef.current === selectedRing) return;
    previousRingRef.current = selectedRing;
    // A ring switch is a discrete event, not a tick — reading todayKey here
    // (rather than adding it to the dep array) is a one-shot "now", the same
    // idiom AnnouncerContext's own viewingClassId reset uses.
    setShownDayKey(todayKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRing]);

  const spine = useMemo(
    () =>
      buildDaySpine({
        rides,
        breaks,
        classes,
        statuses,
        liveClassId,
        isClassPlacedFromSummary,
        dayKey: shownDayKey,
        todayKey,
      }),
    [rides, breaks, classes, statuses, liveClassId, isClassPlacedFromSummary, shownDayKey, todayKey],
  );

  // VIEWING is null ("following the ring") until an explicit click pins it —
  // doc §11: "they usually sit on the same row" is exactly this default.
  const effectiveViewingClassId = viewingClassId ?? liveClassId;

  // Compress-then-scroll ladder (AC4/AC14/AC15/AC16). Measures the FIXED
  // rows container itself (never the content — that would be a CSS reflow
  // feedback loop), then picks density/scroll off the entry count against
  // that measured height. Starts at density "normal" before the first
  // measurement lands so a fixture that fits never flashes compressed.
  const rowsContainerRef = useRef(null);
  const [rowsAreaHeight, setRowsAreaHeight] = useState(null);
  useEffect(() => {
    const el = rowsContainerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver((entriesList) => {
      const entry = entriesList[0];
      if (entry) setRowsAreaHeight(entry.contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const rowCount = spine.entries.length;
  const fitsNormal = rowsAreaHeight == null || rowCount * ROW_HEIGHT_NORMAL <= rowsAreaHeight;
  const fitsCompressed = rowsAreaHeight == null || rowCount * ROW_HEIGHT_COMPRESSED <= rowsAreaHeight;
  const density = fitsNormal ? "normal" : "compressed";
  const scrollEnabled = !fitsNormal && !fitsCompressed;

  // #2140 AC15/AC16 — this tracks whether the RUNNING row is out of view so
  // an edge chip can offer to jump to it, but it may ONLY ever call
  // setState. It never calls scrollIntoView/sets scrollTop itself — only
  // goToRunning (the chip's own onClick, below) does that, so the band never
  // moves on its own when RUNNING advances.
  const [runningRowEl, setRunningRowEl] = useState(null);
  const [runningOffscreen, setRunningOffscreen] = useState(false);
  useEffect(() => {
    if (
      !scrollEnabled ||
      !runningRowEl ||
      !rowsContainerRef.current ||
      typeof IntersectionObserver === "undefined"
    ) {
      setRunningOffscreen(false);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setRunningOffscreen(!entry.isIntersecting),
      { root: rowsContainerRef.current, threshold: 0 },
    );
    observer.observe(runningRowEl);
    return () => observer.disconnect();
  }, [scrollEnabled, runningRowEl]);

  const goToRunning = useCallback(() => {
    runningRowEl?.scrollIntoView({ block: "nearest" });
  }, [runningRowEl]);

  // #3392 -- "Go To Today" lives on the date line and exists only while the
  // shown day is not today. The header is a fixed height either way, so the
  // rows below never shift.
  const showGoToToday = shownDayKey !== todayKey;

  return (
    <Box data-testid="day-spine" sx={{ position: "relative", display: "flex", flexDirection: "column", height: "100%" }}>
      <Box
        data-testid="day-spine-header"
        sx={{
          height: HEADER_HEIGHT,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          pl: 0.5,
          pr: 1,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <IconButton
          data-testid="day-spine-prev-day"
          size="small"
          aria-label={t("daySpine.prevDay")}
          onClick={() => setShownDayKey((k) => shiftDayKey(k, -1))}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Typography data-testid="day-spine-day-label" variant="subtitle2" noWrap>
          {formatDaySwitcherLabel(shownDayKey)}
        </Typography>
        <IconButton
          data-testid="day-spine-next-day"
          size="small"
          aria-label={t("daySpine.nextDay")}
          onClick={() => setShownDayKey((k) => shiftDayKey(k, 1))}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
        {showGoToToday && (
          <Button
            data-testid="day-spine-go-to-today"
            variant="outlined"
            size="small"
            onClick={() => setShownDayKey(todayKey)}
            sx={{
              ml: "auto",
              flexShrink: 0,
              textTransform: "none",
              fontSize: 12,
              lineHeight: 1.3,
              py: 0.25,
              px: 1,
              minWidth: 0,
              color: colors.foxOrange,
              borderColor: colors.foxOrange,
              "&:hover": { borderColor: colors.foxOrange, bgcolor: "rgba(248, 153, 29, 0.08)" },
            }}
          >
            {t("daySpine.goToToday")}
          </Button>
        )}
      </Box>

      <Box
        ref={rowsContainerRef}
        data-testid="day-spine-rows"
        data-density={density}
        data-scroll-enabled={scrollEnabled ? "true" : "false"}
        sx={{ flex: 1, minHeight: 0, overflowY: scrollEnabled ? "auto" : "hidden", py: 0.5 }}
      >
        {rowsLoading ? (
          <Box data-testid="day-spine-loading" role="status" sx={{ px: 1, pt: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              {t("daySpine.loading")}
            </Typography>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rounded" animation={false} height={ROW_HEIGHT_NORMAL - 12} sx={{ mb: 1 }} />
            ))}
          </Box>
        ) : null}
        {!rowsLoading && spine.entries.length === 0 ? (
          // #2702 -- a loaded day with nothing on it says so, so a blank
          // list can only ever mean "not loaded" (which has its own state).
          // A failed rides/breaks fetch leaves them null (the same shape as a
          // genuinely empty day), so hasFetchError (RingDataContext) swaps in
          // the "could not load" copy rather than a false "no classes"
          // (#2233; same guard as OrderOfGo's oog.emptyFetchError).
          <Typography
            data-testid={hasFetchError ? "day-spine-fetch-error" : "day-spine-empty"}
            role="status"
            variant="body2"
            color="text.secondary"
            sx={{ px: 1, pt: 1.5 }}
          >
            {hasFetchError ? t("daySpine.emptyFetchError") : t("daySpine.empty")}
          </Typography>
        ) : null}
        {!rowsLoading && spine.entries.map((entry) => {
          if (entry.kind === "BREAK") {
            return <BreakEntryRow key={entry.id} entry={entry} density={density} t={t} />;
          }
          const isViewing = entry.classId === effectiveViewingClassId;
          return (
            <ClassRow
              key={entry.id}
              entry={entry}
              isViewing={isViewing}
              density={density}
              t={t}
              onSelect={() => {
                setViewingClassId(entry.classId);
                // #2404 -- a class click also opens the narrow-mode look-up
                // overlay (no-op in the wide layout, where it is always open).
                openLookup();
              }}
              rowRef={entry.isRunning ? setRunningRowEl : undefined}
            />
          );
        })}
      </Box>

      {scrollEnabled && runningOffscreen && (
        <Chip
          data-testid="day-spine-offscreen-chip"
          label={t("daySpine.jumpToRunning")}
          size="small"
          onClick={goToRunning}
          sx={{
            position: "absolute",
            right: 8,
            bottom: 8,
            bgcolor: colors.foxOrange,
            color: "#fff",
            cursor: "pointer",
            zIndex: 1,
          }}
        />
      )}
    </Box>
  );
}
